import React from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Switch,
    Typography,
    Divider,
    IconButton,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import SettingsIcon from "@mui/icons-material/Settings";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import LogoutIcon from "@mui/icons-material/Logout";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation } from "react-router";
import useAdminStore from "../store/adminStore";

const Sidebar = ({ darkMode, onToggleDarkMode, onLogout, mobileOpen, onMobileClose }) => {
    const location = useLocation();
    const role = useAdminStore((s) => s.role);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const allItems = [
        { text: "Dashboard", icon: <DashboardIcon />, path: "/" },
        { text: "Users", icon: <PeopleIcon />, path: "/users" },
        { text: "Companies", icon: <BusinessIcon />, path: "/companies" },
        { text: "Tips", icon: <AttachMoneyIcon />, path: "/tips" },
        { text: "Config", icon: <MiscellaneousServicesIcon />, path: "/config" },
        {},
        {
            text: "Analytics",
            icon: <AnalyticsIcon />,
            path: process.env.REACT_APP_ANALYTICS_LINK,
            external: true,
        },
        {
            text: "Remote Config",
            icon: <SettingsIcon />,
            path: process.env.REACT_APP_REMOTE_CONFIG_LINK,
            external: true,
        },
    ];
    const menuItems = role === "admin" ? allItems.filter((i) => i.path === "/users") : allItems;

    const handleDrawerToggle = () => {
        if (isMobile) {
            onMobileClose();
        }
    };

    const drawerContent = (
        <div style={{ width: 240, display: "flex", flexDirection: "column", height: "100%" }}>
            <Typography
                variant="h5"
                align="center"
                sx={{ my: 2, fontWeight: "bold", color: "primary.main" }}
            >
                ThanQ Dashboard
            </Typography>
            <List>
                {menuItems.map((item) =>
                    item.text ? (
                        <ListItem
                            key={item?.text}
                            component={item.external ? "a" : Link}
                            to={item.external ? undefined : item.path}
                            href={item.external ? item.path : undefined}
                            target={item.external ? "_blank" : undefined}
                            rel={item.external ? "noopener noreferrer" : undefined}
                            selected={!item.external && location.pathname === item.path}
                            onClick={handleDrawerToggle}
                            sx={{
                                textDecoration: "none",
                                color: "inherit",
                                backgroundColor:
                                    !item.external && location.pathname === item.path
                                        ? "rgba(240, 85, 19, 0.5) !important"
                                        : "transparent",
                                "&.Mui-selected": {
                                    backgroundColor: "rgba(240, 85, 19, 0.5) !important",
                                    color: "primary.main",
                                    "&:hover": {
                                        backgroundColor: "rgba(240, 85, 19, 0.2)",
                                    },
                                },
                                "& a": {
                                    textDecoration: "none",
                                    color: "inherit",
                                },
                            }}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                            {item.external && <LinkIcon />}
                        </ListItem>
                    ) : (
                        <Divider />
                    )
                )}
            </List>
            <div style={{ flexGrow: 1 }} />
            <Divider />
            <List>
                <ListItem button onClick={onLogout} sx={{ cursor: "pointer" }}>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                </ListItem>
                <ListItem>
                    <ListItemText primary="Dark Mode" />
                    <Switch checked={darkMode} onChange={onToggleDarkMode} />
                </ListItem>
            </List>
        </div>
    );

    return (
        <>
            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                anchor="left"
                open={isMobile ? mobileOpen : true}
                onClose={onMobileClose}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                    display: { xs: "block", md: "block" },
                    "& .MuiDrawer-paper": {
                        boxSizing: "border-box",
                        width: 240,
                    },
                }}
            >
                {drawerContent}
            </Drawer>
        </>
    );
};

export default Sidebar;

// Export hamburger menu button component for use in App.js
export const SidebarToggle = ({ onMobileOpen }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    if (!isMobile) return null;

    return (
        <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMobileOpen}
            sx={{ mr: 2, display: { md: "none" } }}
        >
            <MenuIcon />
        </IconButton>
    );
};
